import { useState } from "react";
import { Search, AlertTriangle, RotateCcw, CheckCircle2 } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Modal } from "./shared";

export function VipBonusReconcilePanel() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const search = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults(null); return; }
    try {
      const r = await api.get(`/admin/vip-bonus-lookup?q=${encodeURIComponent(val.trim())}`);
      setResults(r.data.users || []);
    } catch (e) { toast.error(apiErr(e)); setResults([]); }
  };

  const reverse = async (u) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/vip-bonus-reverse", { user_id: u.user_id });
      toast.success(`Reversed ${r.data.deducted.toLocaleString()} gold from ${u.name}.`);
      setConfirm(null);
      setResults((rs) => rs.map((x) => (x.user_id === u.user_id ? r.data.user : x)));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="vip-recon-panel" className="mb-6 border border-amber-700/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-2">
        <GoldCoin size={16} className="text-amber-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">VIP Welcome-Bonus Reversal</h3>
      </div>
      <p className="text-sm text-zinc-500 mb-3">
        Reverse a <span className="text-amber-300">1,500 gold</span> VIP welcome bonus that was granted in error (e.g. to a player who was already VIP).
        Search a player, then reverse it. <span className="text-zinc-400">"Bonus Grants" counts the welcome-bonus inbox messages on record — it can show 0 if the player cleared their inbox, so reverse based on who you know was affected.</span> Balances are never pushed negative, and each player can only be reversed once.
      </p>

      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input data-testid="vip-recon-search" value={q} onChange={(e) => search(e.target.value)}
          placeholder="Search player by name or email…"
          className="w-full bg-zinc-900 border border-zinc-800 pl-9 pr-3 py-2 text-zinc-100 text-sm focus:border-amber-600 outline-none rounded-sm" />
      </div>

      {results !== null && (
        <div data-testid="vip-recon-results" className="mt-3 overflow-x-auto border border-zinc-800 rounded-sm">
          {results.length === 0 ? (
            <div className="px-3 py-3 text-xs text-zinc-600">No players found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-900/60">
                  <th className="text-left px-3 py-2">Player</th>
                  <th className="text-center px-3 py-2">VIP</th>
                  <th className="text-right px-3 py-2">Bonus Grants</th>
                  <th className="text-right px-3 py-2">Gold</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {results.map((u) => (
                  <tr key={u.user_id} data-testid={`vip-recon-row-${u.user_id}`} className="border-t border-zinc-900">
                    <td className="px-3 py-2 text-zinc-200">{u.name} <span className="text-zinc-600 text-xs">· {u.email}</span></td>
                    <td className="px-3 py-2 text-center">{u.vip ? <span className="text-amber-400">Yes</span> : <span className="text-zinc-600">No</span>}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{u.bonus_grants}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{u.gold.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">
                      {u.reversed ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-400" data-testid={`vip-recon-done-${u.user_id}`}>
                          <CheckCircle2 size={13} /> Reversed (-{u.reversed_amount.toLocaleString()})
                        </span>
                      ) : (
                        <button data-testid={`vip-recon-reverse-${u.user_id}`} onClick={() => setConfirm(u)} disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-cinzel uppercase tracking-wide rounded-sm border border-amber-600 text-amber-300 hover:bg-amber-900/20 disabled:opacity-40">
                          <RotateCcw size={13} /> Reverse
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {confirm && (
        <Modal title="Confirm VIP Bonus Reversal" testid="vip-recon-confirm" onClose={() => setConfirm(null)}>
          <div className="flex items-start gap-2 text-sm text-zinc-300">
            <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <p>
              Reverse the VIP welcome bonus for <span className="text-amber-300 font-semibold">{confirm.name}</span>?
              This deducts up to <span className="text-amber-300 font-semibold">{((confirm.bonus_grants || 1) * 1500).toLocaleString()} gold</span> (clamped to their current {confirm.gold.toLocaleString()}g).
              The player is notified and can't be reversed again.
            </p>
          </div>
          <div className="mt-5 flex gap-2 justify-end">
            <button data-testid="vip-recon-cancel" onClick={() => setConfirm(null)} disabled={busy} className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm">Cancel</button>
            <button data-testid="vip-recon-confirm-btn" onClick={() => reverse(confirm)} disabled={busy} className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm disabled:opacity-50">{busy ? "Reversing…" : "Confirm Reversal"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
